export interface NormalizeVendorSourceIdOptions {
  /**
   * When true (DRB / customerCode barcodes), only trim — do not strip
   * hyphens/underscores/dots. DRB codes like `9CLD067-CA` or `63223+651195`
   * must match customers.source_id exactly.
   */
  preserveSeparators?: boolean;
}

/** Strip spaces, hyphens, underscores, and dots from a vendor barcode value. */
export function normalizeVendorSourceId(
  value: string | null | undefined,
  options: NormalizeVendorSourceIdOptions = {}
): string {
  const trimmed = String(value ?? '').trim();
  if (options.preserveSeparators) {
    return trimmed;
  }
  return trimmed.replace(/[\s\-_.]/g, '');
}

/**
 * Read a query parameter without letting '+' become a space.
 *
 * URLSearchParams applies form decoding, so a DRB tag encoding `?customerCode=31917+584377`
 * reads back as `31917 584377`. That silently rewrote the membership id and the customer
 * could never be found, while a code without a '+' scanned fine (CWS-1030).
 */
function readRawQueryParam(url: URL, key: string): string | null {
  const query = url.search.startsWith('?') ? url.search.slice(1) : url.search;
  for (const pair of query.split('&')) {
    if (!pair) {
      continue;
    }
    const separatorIndex = pair.indexOf('=');
    const rawKey = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
    if (decodeURIComponent(rawKey) !== key) {
      continue;
    }
    const rawValue = separatorIndex === -1 ? '' : pair.slice(separatorIndex + 1);
    try {
      // decodeURIComponent leaves '+' alone, unlike URLSearchParams.
      return decodeURIComponent(rawValue) || null;
    } catch {
      return rawValue || null;
    }
  }
  return null;
}

/**
 * Extract a vendor source_id from raw barcode text (plain numeric, formatted, or URL).
 */
export function extractVendorSourceIdFromBarcode(
  raw: string | null | undefined,
  options: NormalizeVendorSourceIdOptions = {}
): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      const fromQuery =
        readRawQueryParam(url, 'source_id') ||
        readRawQueryParam(url, 'sourceId') ||
        readRawQueryParam(url, 'customer_id') ||
        readRawQueryParam(url, 'customerId') ||
        readRawQueryParam(url, 'customerCode') ||
        readRawQueryParam(url, 'customer_code');
      if (fromQuery) {
        return normalizeVendorSourceId(fromQuery, options);
      }
      const segments = url.pathname.split('/').filter(Boolean);
      if (segments.length > 0) {
        return normalizeVendorSourceId(segments[segments.length - 1], options);
      }
    } catch {
      // Fall through to plain normalization.
    }
  }

  return normalizeVendorSourceId(trimmed, options);
}
